import { Box, Tooltip } from "@mui/material";
import { Icon, paletteVar, paletteVarAlpha } from "@platform/design-system";

import { enrollmentFlagLevel } from "../../../utils/enrollmentFlagLevel";
import { useEnrollmentFlagState } from "../EnrollmentFlagContext";

import type { FC, ReactNode } from "react";
import type { Rate } from "../../../types";
import type { EnrollmentMeasure } from "../../../utils/quotedEnrollmentReview";

const MISMATCH_TOOLTIP =
  "Mismatch with current enrollment. Resolve in rates actions menu.";

/** The group's total is the more serious mismatch, so it is red and a rate's own is orange. */
const FLAG_STYLE = {
  group: {
    tint: paletteVarAlpha(paletteVar("error-main", "#D32F2F"), 0.14),
    icon: "warning",
  },
  rate: {
    tint: paletteVarAlpha(paletteVar("warning-main", "#ED6C02"), 0.16),
    icon: "warning",
  },
} as const;

/**
 * The input is right-aligned, so the icon sits at the left edge, clear of the value, and
 * ignores the pointer so a click still lands in the input.
 */
const ICON_SX = {
  position: "absolute",
  left: 4,
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  pointerEvents: "none",
  zIndex: 1,
} as const;

export interface EnrollmentFlagProps {
  /** The rate the cell belongs to; absent while the plan has none yet. */
  rate: Rate | undefined;
  /** What the cell shows: the lives enrolled, or the volume the rate is priced on. */
  measure: EnrollmentMeasure;
  /** The enrolled-lives or volume input the flag marks. */
  children: ReactNode;
}

/**
 * Marks an enrolled cell whose carrier figure differs from Current's, while the analyst still
 * owes a decision. A cell with nothing to flag renders its input untouched.
 *
 * The Broker Portal theme paints every input root opaque white, so the tint has to reach
 * the input as well or it stays hidden behind it.
 */
export const EnrollmentFlag: FC<EnrollmentFlagProps> = ({
  rate,
  measure,
  children,
}) => {
  const state = useEnrollmentFlagState();
  const level = rate ? enrollmentFlagLevel(state, rate, measure) : undefined;

  if (!level) {
    return <>{children}</>;
  }

  const style = FLAG_STYLE[level];

  return (
    <Tooltip title={MISMATCH_TOOLTIP}>
      <Box
        data-testid="enrollment-flag"
        data-flag-level={level}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          backgroundColor: style.tint,
          "& .MuiInputBase-root": { backgroundColor: "transparent" },
        }}
      >
        <Box sx={ICON_SX}>
          <Icon
            name={style.icon}
            color={level === "group" ? "error" : "warning"}
            aria-label="Mismatch with current enrollment"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Tooltip>
  );
};
